import type { BufferAttribute } from 'three';

/** Authored shaved-hair coverage, carried by the hairstyle asset in head rest space. */
export interface HairScalpTint {
  /** Zero paints both sides (buzz cuts / undercuts). */
  side: -1 | 0 | 1;
  minX: number;
  density: number;
  /** Ordered [forward Z, lower hairline Y] stations, from nape to forehead. */
  border: readonly (readonly [number, number])[];
}

function insideCrop(tint: HairScalpTint, x: number, y: number, z: number): boolean {
  if ((tint.side === 0 ? Math.abs(x) : x * tint.side) <= tint.minX || Math.abs(x) > .10 || y < 1.56 || y > 1.80) return false;
  const border = tint.border;
  if (z < border[0][0] || z > border[border.length - 1][0]) return false;
  let i = 0;
  while (i < border.length - 2 && z > border[i + 1][0]) i++;
  const [az, ay] = border[i], [bz, by] = border[i + 1];
  const lower = ay + (by - ay) * (z - az) / (bz - az);
  return y >= lower;
}

/** Select complete exported skin facets. The head exporter keeps distinct triangle
 * corners, allowing a sharp painted edge without splitting or offsetting the mesh. */
export function scalpHairFacets(tint: HairScalpTint, position: BufferAttribute, index: BufferAttribute): Uint32Array {
  const selected = new Set<number>();
  for (let i = 0; i < index.count; i += 3) {
    const corners = [index.getX(i), index.getX(i + 1), index.getX(i + 2)];
    // Keep the opposite side and the neck wholly outside the crop.
    if (corners.some(v => (tint.side === 0 ? Math.abs(position.getX(v)) : position.getX(v) * tint.side) <= tint.minX || Math.abs(position.getX(v)) > .10 || position.getY(v) < 1.56)) continue;
    const x = corners.reduce((s, v) => s + position.getX(v), 0) / 3;
    const y = corners.reduce((s, v) => s + position.getY(v), 0) / 3;
    const z = corners.reduce((s, v) => s + position.getZ(v), 0) / 3;
    if (insideCrop(tint, x, y, z)) for (const v of corners) selected.add(v);
  }
  return Uint32Array.from(selected);
}
